import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import axios from 'axios';
import { updateUser, checkDuplicateEmail, getRoles, getBusiness } from '../api/adminApi';
import { IoCloseOutline } from "react-icons/io5";

const statusOptions = [
  { value: '1', label: 'Active' },
  { value: '2', label: 'Inactive' }
];

const UserForm = ({
  mode = 'create',
  user = {},
  handleCloseModal,
  onSuccess
}) => {
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [accessCategory, setAccessCategory] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState([]);
  const [roles, setRoles] = useState([]);
  const [businessOptions, setBusinessOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const userMail = localStorage.getItem("userId"); 

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await getRoles();
      if (Array.isArray(data)) {
        const formattedRoles = data.map(r => ({
          label: r.name,
          value: r.value
        }));
        setRoles(formattedRoles);
      } else {
        setRoles([]);
        console.warn('Invalid roles response:', data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  }; 

  const fetchBusiness = async () => {
    try {
      setLoading(true);
      const data = await getBusiness();
      if (Array.isArray(data)) {
        const formattedBusiness = data.map(b => ({
          label: b.name,
          value: b.value
        }));
        setBusinessOptions(formattedBusiness);
      } else {
        setBusinessOptions([]);
        console.warn('Invalid business response:', data);
      }
    } catch (error) {
      console.error('Error fetching business:', error);
      toast.error('Failed to fetch business data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchRoles(), fetchBusiness()]);
  }, []);

  
  useEffect(() => {
    if (mode === 'edit' && user) {
      setUserName(user.name || '');
      setUserEmail(user.emailId || '');
      setAccessCategory(user.accessCategory || '');

      // Match Role
      if (roles.length > 0) {
        const matchedRole = roles.find(r => r.label === user.role);
        setSelectedRole(matchedRole || null);
      }

      // Match Status
      const matchedStatus = statusOptions.find(
        s => s.label.toLowerCase() === (user.status || '').toLowerCase()
      );
      setSelectedStatus(matchedStatus || null);

      // Match Business
      if (businessOptions.length > 0) {
        const matchedBusiness = user.business
          ? user.business
              .split(',')
              .map(b => businessOptions.find(o => o.label === b.trim()))
              .filter(Boolean)
          : [];
        setSelectedBusiness(matchedBusiness);
      }
    }
  }, [user, mode, roles, businessOptions]);

  
  const validateForm = () => {
    const formErrors = {};
    if (!userName.trim()) formErrors.userName = 'Please enter the user name';
    if (!userEmail || !userEmail.includes('@')) formErrors.userEmail = 'Please enter a valid email';
    if (!selectedRole) formErrors.selectedRole = 'Please select a role';
    if (!selectedStatus) formErrors.selectedStatus = 'Please select a status';
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  /** Handle Submit */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    const userPayload = {
      Sno: user?.id || "",
      Name: userName,
      EmailId: userEmail,
      Role: selectedRole?.label || '',
      Status: selectedStatus?.label || '',
      Business: selectedBusiness.map(b => b.label).join(','),
      AccessCategory: accessCategory,
      CreaterId: userMail
    };

    try {
      if (mode === 'create') {
        const duplicate = await checkDuplicateEmail(userEmail);
        if (duplicate) {
          toast.error('A user with this email already exists');
          return;
        }
        await axios.post("/jupiterdata/InsertUpdateUser", userPayload);
        toast.success('User created successfully!');
      } else {
        await updateUser(userPayload);
        toast.success('User updated successfully!');
      }

      if (onSuccess) await onSuccess();
      handleCloseModal();
    } catch (error) {
      console.error(`${mode === 'create' ? 'Create' : 'Edit'} Error:`, error);
      toast.error(`Failed to ${mode === 'create' ? 'create' : 'update'} user`);
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = loading
    ? mode === 'create'
      ? 'Adding...'
      : 'Updating...'
    : mode === 'create'
      ? 'Add'
      : 'Update';

  return (
    <form onSubmit={handleSubmit} noValidate>
      <button
        className='btn modal-close'
        onClick={handleCloseModal}
        type='button'
        disabled={loading}
      >
        <IoCloseOutline />
      </button>

      <div className='form-block'>
        <div className='two-col-form'>
          {/* User Name */}
          <div className="form-group">
            <label>User Name<span className="required">*</span></label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              disabled={loading || mode === 'edit'}
              className={`form-control ${errors.userName ? 'is-invalid' : ''}`}
            />
            {errors.userName && <span className="error">{errors.userName}</span>}
          </div>

          {/* Email */}
          <div className="form-group">
            <label>User Email<span className="required">*</span></label>
            <input
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              disabled={loading || mode === 'edit'}
              className={`form-control ${errors.userEmail ? 'is-invalid' : ''}`}
            />
            {errors.userEmail && <span className="error">{errors.userEmail}</span>}
          </div>

          {/* Role */}
          <div className="form-group">
            <label>Select Role<span className="required">*</span></label>
            <Select
              options={roles}
              value={selectedRole}
              onChange={setSelectedRole}
              isDisabled={loading}
              classNamePrefix="custom-select"
            />
            {errors.selectedRole && <span className="error">{errors.selectedRole}</span>}
          </div>

          {/* Status */}
          <div className="form-group">
            <label>Select Status<span className="required">*</span></label>
            <Select
              options={statusOptions}
              value={selectedStatus}
              onChange={setSelectedStatus}
              isDisabled={loading}
              classNamePrefix="custom-select"
            />
            {errors.selectedStatus && <span className="error">{errors.selectedStatus}</span>}
          </div>
        </div>

        {/*Dynamic Business Options */}
        <div className="form-group">
          <label>Select Business</label>
          <Select
            options={businessOptions}
            value={selectedBusiness}
            isMulti
            onChange={setSelectedBusiness}
            isDisabled={loading}
            classNamePrefix="custom-select"
          />
        </div>

        <div className='form-group'>
          <label>Access Category</label>
          <input
            type="text"
            value={accessCategory}
            onChange={(e) => setAccessCategory(e.target.value)}
            disabled={loading}
            className="form-control"
          />
        </div>
      </div>

      <div className='modal-footer'>
        <div className='modal-footer-btns'>
          <button
            className='btn btn-outline'
            type='button'
            onClick={handleCloseModal}
            disabled={loading}
          >
            Cancel
          </button>
          <button className='btn btn-primary' type='submit' disabled={loading}>
            {submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default UserForm;
